import {
  GridActionsCellItem,
  GridRowParams,
} from '@mui/x-data-grid';
import { Paper, Grid, Tooltip, Button } from '@mui/material';
import {
  Edit as EditIcon,
  Visibility,
  FilterNone as DeduplicateIcon,
  PhotoSizeSelectSmall as ThumbnailIcon,
} from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import DataList from '../../components/DataList';
import useCheckPermission from '../../hooks/useCheckPermission';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import systemConfigurationService from '../../services/systemConfigurationService';
import imageService from '../../services/imageService';

export interface SystemConfiguration {
  id: number;
  key: string;
  value: string;
  description: string;
}

const SystemConfigurationsPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<any[]>([]);
  const [confirmTitle, setConfirmTitle] = useState('Megerősítés');
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch<any>();
  const { checkPermission } = useCheckPermission();
  const { ConfirmDialog, setParams: setConfirmParams, params: confirmParams } =
    useConfirmDialog();

  useEffect(() => {
    dispatch({ type: 'SHOW_QUERY' });
    systemConfigurationService
      .list()
      .then((response) => {
        if (!response.hasError) setRows(response.records);
        else
          enqueueSnackbar(response.errorMessages.join(','), {
            variant: 'error',
          });
      })
      .finally(() => dispatch({ type: 'HIDE' }));
  }, []);

  const handleDeduplicateImages = () => {
    setConfirmTitle('Képek deduplikálása');
    setConfirmParams({
      open: true,
      name: 'A művelet a duplikált képeket összefésüli, a felesleges példányokat soft-deleteolja és a blobokat törli. A hivatkozások (FK + beágyazott HTML URL-ek) a megmaradó képre kerülnek. Folytatod?',
      onConfirm: () => {
        dispatch({ type: 'SHOW_QUERY' });
        return imageService
          .deduplicate()
          .then((response: any) => {
            if (response?.hasError) {
              enqueueSnackbar(
                (response.errorMessages || ['Ismeretlen hiba']).join(','),
                { variant: 'error' }
              );
              return;
            }

            enqueueSnackbar(
              `Deduplikáció kész: ${response.duplicateGroupsMerged ?? 0} csoport, ${response.imagesSoftDeleted ?? 0} kép törölve, ${response.blobsDeleted ?? 0} blob, ${response.referencesRetargeted ?? 0} referencia, ${response.embeddedHtmlRewritten ?? 0} HTML átírva.`,
              { variant: 'success' }
            );
          })
          .catch(() => {
            enqueueSnackbar('A képek deduplikációja sikertelen!', {
              variant: 'error',
            });
          })
          .finally(() => dispatch({ type: 'HIDE' }));
      },
    });
  };

  const handleRegenerateThumbnails = () => {
    setConfirmTitle('Thumbnailök újragenerálása');
    setConfirmParams({
      open: true,
      name: 'Minden képhez új, kisebb (256px JPEG) gallery thumbnail készül az original / crop alapján. Ez eltarthat néhány percig. Folytatod?',
      onConfirm: () => {
        dispatch({ type: 'SHOW_QUERY' });
        return imageService
          .regenerateThumbnails(true)
          .then((response: any) => {
            if (response?.hasError) {
              enqueueSnackbar(
                (response.errorMessages || ['Ismeretlen hiba']).join(','),
                { variant: 'error' }
              );
              return;
            }

            enqueueSnackbar(
              `Thumbnailök kész: ${response.generatedCount ?? 0} generálva, ${response.skippedCount ?? 0} kihagyva, ${response.failedCount ?? 0} hiba.`,
              { variant: 'success' }
            );
          })
          .catch(() => {
            enqueueSnackbar('A thumbnail generálás sikertelen!', {
              variant: 'error',
            });
          })
          .finally(() => dispatch({ type: 'HIDE' }));
      },
    });
  };

  const getActions = (params: GridRowParams) => {
    var actions: any[] = [];

    let readonly = !checkPermission(['ManageSystemConfigurations']);
    actions.push(
      <GridActionsCellItem
        color="primary"
        icon={
          readonly ? (
            <Tooltip title="View">
              <Visibility />
            </Tooltip>
          ) : (
            <Tooltip title="Edit">
              <EditIcon />
            </Tooltip>
          )
        }
        label={readonly ? 'View' : 'Edit'}
        onClick={() => navigate(`/systemConfigurations/edit/${params.row.id}`)}
      />
    );

    return actions;
  };

  const columns: any[] = [
    { field: 'key', headerName: 'Key', flex: 100 },
    {
      field: 'description',
      headerName: 'Description',
      flex: 100,
    },
    {
      field: 'value',
      headerName: 'Value',
      flex: 100,
    },
    {
      field: 'actions',
      type: 'actions',
      flex: 20,
      getActions: getActions,
    } as any,
  ];

  return (
    <Paper>
      <Grid container sx={{
        p: 3
      }}>
        <Grid size={12}>
          <h2>SystemConfigurations</h2>
        </Grid>
        {(checkPermission(['DeleteImage']) ||
          checkPermission(['EditImage'])) && (
          <Grid
            container
            spacing={1}
            size={12}
            sx={{
              justifyContent: "end",
              pb: 2
            }}>
            {checkPermission(['EditImage']) && (
              <Grid>
                <Tooltip title="Kis JPEG thumbnailök generálása minden képhez (galéria csempék)">
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<ThumbnailIcon />}
                    onClick={handleRegenerateThumbnails}
                  >
                    Thumbnailök újragenerálása
                  </Button>
                </Tooltip>
              </Grid>
            )}
            {checkPermission(['DeleteImage']) && (
              <Grid>
                <Tooltip title="Duplikált galéria képek összefésülése (hash alapján)">
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<DeduplicateIcon />}
                    onClick={handleDeduplicateImages}
                  >
                    Képek deduplikálása
                  </Button>
                </Tooltip>
              </Grid>
            )}
          </Grid>
        )}
        <Grid size={12}>
          <DataList rows={rows} columns={columns} />
        </Grid>
        <ConfirmDialog
          actionName={confirmTitle}
          dialogTitle={confirmParams.name}
        />
      </Grid>
    </Paper>
  );
};

export default SystemConfigurationsPage;
